import React, { createContext, useContext } from 'react';
import type { Board, Item } from '@/stores/canvasStore';

/**
 * What a Library surface can do to an item, supplied by whoever mounts the
 * Library: the real store in the app (MobileLibraryContainer), no-ops with
 * toasts in the dev fixture. Deep components (the detail view, the add
 * sheet) read it from context instead of importing the store, so every
 * Library component renders in the fixture with no network.
 */
export interface LibraryActions {
  /** Asks first; true when the item was deleted. */
  deleteItem: (item: Item) => Promise<boolean>;
  /** Move an item into one of the user's boards. */
  moveToBoard: (item: Item, boardId: string) => Promise<void>;
  /** Make a new space: a board inside Home, with its folder on the Home canvas. Resolves with it, or null. */
  createSpace?: (name: string) => Promise<Board | null>;
  /** Save a pasted or typed link. Resolves with the new item, or null. */
  addLink: (url: string) => Promise<Item | null>;
  /** Save a short text note. */
  addNote: (text: string) => Promise<Item | null>;
  /** Write an edited note's body (TipTap HTML) back to its item. */
  updateNote: (item: Item, body: string) => Promise<void>;
  /** Upload photos or files picked on the phone. */
  addFiles: (files: File[]) => Promise<void>;
  /** Re-read items after something changed outside the store. */
  refresh: () => Promise<void>;
  /**
   * Fetch a missing or stale preview in the background (Instagram and TikTok
   * links saved without a picture, pulled creatives with expired thumbnails),
   * the way desktop cards do. Fire and forget; the store updates the item.
   */
  refreshPreview?: (item: Item, mode: PreviewRefreshMode) => void;
}

/** 'free' and 'deep' link refreshes (see needsPreviewRefresh), or re-hosting a creative's thumbnail. */
export type PreviewRefreshMode = 'free' | 'deep' | 'creative';

/** A note left on a saved item. It belongs to the item (item_comments), so it moves and is deleted with it. */
export interface ItemComment {
  id: string;
  itemId: string;
  body: string;
  createdAt: string;
}

/** Comments, through the same seam as the actions: Supabase in the app, memory in the fixture. */
export interface LibraryComments {
  /** Comment count per item id, for the badge on tiles. */
  counts: Record<string, number>;
  list: (itemId: string) => Promise<ItemComment[]>;
  add: (itemId: string, body: string) => Promise<ItemComment | null>;
  remove: (comment: ItemComment) => Promise<boolean>;
}

export interface LibraryContextValue {
  actions: LibraryActions;
  boards: Board[];
  comments: LibraryComments;
  /** Items saved a moment ago whose preview is still being fetched: their tiles develop. */
  pending?: ReadonlySet<string>;
}

const noop = async () => null;

export const FIXTURE_ACTIONS: LibraryActions = {
  deleteItem: async () => false,
  moveToBoard: async () => {},
  addLink: noop,
  addNote: noop,
  updateNote: async () => {},
  addFiles: async () => {},
  refresh: async () => {},
};

export const NO_COMMENTS: LibraryComments = {
  counts: {},
  list: async () => [],
  add: async () => null,
  remove: async () => false,
};

const LibraryContext = createContext<LibraryContextValue>({ actions: FIXTURE_ACTIONS, boards: [], comments: NO_COMMENTS });

export function LibraryProvider({ value, children }: { value: LibraryContextValue; children: React.ReactNode }) {
  return <LibraryContext.Provider value={value}>{children}</LibraryContext.Provider>;
}

export function useLibrary(): LibraryContextValue {
  return useContext(LibraryContext);
}
